import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'react-admin';
import { useLocation } from 'react-router-dom';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import MuiMenu from '@material-ui/core/Menu';
import MuiMenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';

import { RootState } from '~/types';
import { getKycCredentials, kycAppLogin, openKycApp } from '~/api/kyc';
import onError from '~/errorsHandler';
import { useGetMambuLedgerStatus } from '~/hooks/ledgers';
import useFeatures from '~/hooks/useFeatures';
import { useStyles } from '~/hooks/useLayoutMenuStyles';
import {
  l,
  Badge,
  Props,
  RouteItem,
  GroupItem,
  GroupChildItem,
  TopLevelEntry,
} from '~/types/layoutMenu';
import { isDisabled, pathActive } from '~/utils/layoutMenu';

import { buildMenuStructure } from './menuStructure';
import MenuItem from './components/MenuItem';
import { FlyoutItem, FlyoutTitle } from './components/FlyoutItem';
import GroupHeader from './components/GroupHeader';
import MenuFooter from './components/MenuFooter';

const Menu = ({ permissions }: Props) => {
  useSelector<RootState>((state) => state.theme);
  const open = useSelector<RootState>((state) => state.admin.ui.sidebarOpen) as boolean;
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(key), options);
  const location = useLocation();
  const isRail = !open;

  const [kycLoading, setKycLoading] = useState(false);
  const handleKycAppClick = async () => {
    setKycLoading(true);
    try {
      const {
        data: { username, password },
      } = await getKycCredentials();
      await kycAppLogin(username, password);
      openKycApp();
    } catch (error) {
      onError(error);
    }
    setKycLoading(false);
  };

  const { isSuccess: isMambuActive } = useGetMambuLedgerStatus({
    staleTime: 'Infinity',
    onError: () => {},
  });

  const { data: featuresList } = useFeatures();
  const isLicensed = (key: string) =>
    Array.isArray(featuresList) && featuresList.some((f: any) => f.key === key);

  const getBadge = (item: RouteItem): Badge => {
    if (item.licenseKey && !isLicensed(item.licenseKey)) return 'notLicensed';
    if (isDisabled(item, permissions)) return 'notAvailable';
    if (item.isNew) return 'new';
    return null;
  };

  const classes = useStyles({ open });

  const menuStructure = buildMenuStructure({
    handleKycAppClick,
    isMambuActive,
    mambuIconClass: classes.mambuIcon,
  });

  // ── Group open/close state ──────────────────────────────────────────────────
  // Groups can nest inside another group's children to any depth. Every group is
  // tracked in one flat map, keyed by joining its ancestor labelKeys with '.'
  // (e.g. a "Ledgers" group nested in "Integrations" is keyed "integrations.ledgers").
  const topLevelGroups = menuStructure.filter((e): e is GroupItem => e.type === 'group');

  const groupKey = (path: string[]) => path.join('.');

  type GroupEntry = { key: string; parentKey: string | null; group: GroupItem };

  // Recursively collects every GroupItem in the tree, however deep, with its
  // composite key and its immediate parent's key (null for top-level groups).
  // `parentPath` is a local recursion accumulator, not exposed on the result.
  const collectGroups = (
    items: (TopLevelEntry | GroupChildItem)[],
    parentPath: string[]
  ): GroupEntry[] =>
    items
      .filter((c): c is GroupItem => c.type === 'group')
      .flatMap((group) => {
        const path = [...parentPath, group.labelKey];
        return [
          {
            key: groupKey(path),
            parentKey: parentPath.length ? groupKey(parentPath) : null,
            group,
          },
          ...collectGroups(group.children, path),
        ];
      });

  const allGroups = collectGroups(topLevelGroups, []);

  // A group renders nothing if every one of its children is a conditional resolved
  // to hidden — used both to skip rendering it and to keep its parent group from
  // being marked "active" by a route the user can't actually see. Applies uniformly
  // at every depth, including top-level groups: a top-level group whose children are
  // all conditionally hidden should disappear from the menu just like a nested one.
  const isGroupVisible = (group: GroupItem): boolean =>
    group.children.some((child) => child.type !== 'conditional' || child.when);

  // A group's own paths, plus the paths of any currently-visible nested group —
  // recursively, so a route several levels deep still counts toward every ancestor.
  // A hidden nested group's paths never count, so a route the user can't see in the
  // menu (e.g. Mambu inactive) can't falsely highlight an ancestor group as active.
  const groupActivePaths = (group: GroupItem): string[] => [
    ...group.paths,
    ...group.children
      .filter((c): c is GroupItem => c.type === 'group' && isGroupVisible(c))
      .flatMap((child) => groupActivePaths(child)),
  ];

  const openStateFromRoute = () =>
    Object.fromEntries(
      allGroups.map(({ key, group }) => [
        key,
        pathActive(groupActivePaths(group), location.pathname),
      ])
    );

  // Seeded once from the current URL; the effect below keeps this in sync on
  // every subsequent navigation.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(openStateFromRoute);

  const collapseAll = () =>
    setOpenGroups(Object.fromEntries(allGroups.map(({ key }) => [key, false])));

  // Closes every key in `scopeKeys` (an accordion's sibling set), then flips
  // `targetKey` — used at every nesting depth, each accordioning within its own scope.
  const toggleWithinScope = (scopeKeys: string[], targetKey: string) =>
    setOpenGroups((prev) => {
      const next: Record<string, boolean> = { ...prev };
      scopeKeys.forEach((key) => {
        next[key] = false;
      });
      next[targetKey] = !prev[targetKey];
      return next;
    });

  // Toggling a group closes its siblings at the same nesting level (top-level groups
  // close other top-level groups; a nested group closes its sibling nested groups
  // under the same parent) — but leaves the toggled group's own descendants alone,
  // since we're not necessarily closing it (it may be the group being opened).
  const toggleGroup = (key: string, parentKey: string | null) =>
    toggleWithinScope(
      allGroups.filter((g) => g.parentKey === parentKey && g.key !== key).map((g) => g.key),
      key
    );

  // Whenever the route changes — by clicking a link, back/forward, or the sidebar
  // expanding — re-sync which groups are open from the new URL, closing any group
  // that no longer matches. This prevents a group opened by an earlier navigation
  // (e.g. "Ledgers" while on Mambu) from staying open forever once the user has
  // navigated away to an unrelated page.
  useEffect(() => {
    setOpenGroups(openStateFromRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Rail flyout state ───────────────────────────────────────────────────────
  const [flyoutAnchors, setFlyoutAnchors] = useState<Record<string, HTMLElement | null>>(() =>
    Object.fromEntries(topLevelGroups.map((g) => [g.labelKey, null]))
  );
  const closeTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>(
    Object.fromEntries(topLevelGroups.map((g) => [g.labelKey, null]))
  );

  const openFlyout = (labelKey: string, el: HTMLElement) => {
    Object.keys(closeTimers.current).forEach((key) => {
      if (closeTimers.current[key]) {
        clearTimeout(closeTimers.current[key]!);
        closeTimers.current[key] = null;
      }
    });
    setFlyoutAnchors((prev) => {
      const next: Record<string, HTMLElement | null> = Object.fromEntries(
        Object.keys(prev).map((key) => [key, null])
      );
      next[labelKey] = el;
      return next;
    });
  };
  const scheduleFlyoutClose = (labelKey: string) => {
    closeTimers.current[labelKey] = setTimeout(
      () => setFlyoutAnchors((prev) => ({ ...prev, [labelKey]: null })),
      300
    );
  };
  const cancelFlyoutClose = (labelKey: string) => {
    if (closeTimers.current[labelKey]) clearTimeout(closeTimers.current[labelKey]!);
  };
  const closeFlyout = (labelKey: string) =>
    setFlyoutAnchors((prev) => ({ ...prev, [labelKey]: null }));

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderChild = (
    child: GroupChildItem,
    path: string[],
    onClose?: () => void
  ): React.ReactNode => {
    if (child.type === 'conditional') {
      return child.when ? renderChild(child.item, path, onClose) : null;
    }

    if (child.type === 'group') {
      return renderGroup(child, path, onClose);
    }

    if (child.type === 'action') {
      const disabled = child.permission ? !permissions?.includes(child.permission) : false;
      const icon = typeof child.icon === 'function' ? child.icon(kycLoading) : child.icon;

      if (onClose) {
        // flyout variant
        return (
          <MuiMenuItem
            key={child.label}
            disabled={disabled}
            className={classes.flyoutItem}
            onClick={() => {
              child.onClick();
              onClose();
            }}
          >
            <ListItemIcon className={classes.flyoutIcon}>{icon}</ListItemIcon>
            <ListItemText primary={t(child.label)} />
          </MuiMenuItem>
        );
      }

      // collapsed list variant
      return (
        <ListItem key={child.label} button onClick={child.onClick} disabled={disabled}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={t(child.label)} />
        </ListItem>
      );
    }

    // route item
    const badge = getBadge(child);
    const disabled = badge === 'notAvailable';

    if (onClose) {
      return (
        <FlyoutItem
          key={child.path}
          path={child.path}
          label={child.label}
          icon={child.icon}
          disabled={disabled}
          onClose={onClose}
          badge={badge}
          exact={child.exact}
        />
      );
    }

    return (
      <MenuItem
        key={child.path}
        path={child.path}
        label={child.label}
        icon={child.icon}
        disabled={disabled}
        sidebarOpen={open}
        badge={badge}
        exact={child.exact}
      />
    );
  };

  // Single recursive renderer for a group at any nesting depth. `path` is the
  // list of ancestor labelKeys (empty for a top-level group). Only a top-level
  // group in rail mode gets its own hover-triggered flyout popover — nested
  // groups always render as a plain inline accordion row, whether that's within
  // the expanded sidebar or inside an ancestor's flyout (`onClose` is set).
  const renderGroup = (group: GroupItem, path: string[], onClose?: () => void): React.ReactNode => {
    if (!isGroupVisible(group)) return null;

    const key = groupKey([...path, group.labelKey]);
    const isTopLevel = path.length === 0;
    const isGroupOpen = openGroups[key] ?? false;
    const isActive = pathActive(groupActivePaths(group), location.pathname);
    const childPath = [...path, group.labelKey];

    if (isTopLevel && isRail) {
      const anchor = flyoutAnchors[key];
      return (
        <React.Fragment key={key}>
          <GroupHeader
            labelKey={group.labelKey}
            shortLabelKey={group.shortLabelKey}
            icon={group.icon}
            isOpen={isGroupOpen}
            isActive={isActive}
            variant='rail'
            onToggle={() => toggleGroup(key, null)}
            onMouseEnter={(e) => openFlyout(key, e.currentTarget)}
            onMouseLeave={() => scheduleFlyoutClose(key)}
          />
          <MuiMenu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => closeFlyout(key)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            getContentAnchorEl={null}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disableScrollLock
            style={{ pointerEvents: 'none' }}
            PaperProps={{
              style: { pointerEvents: 'auto' },
              onMouseEnter: () => cancelFlyoutClose(key),
              onMouseLeave: () => scheduleFlyoutClose(key),
              className: classes.flyoutPaper,
            }}
          >
            <FlyoutTitle>{t(group.labelKey)}</FlyoutTitle>
            {group.children.map((child) => renderChild(child, childPath, () => closeFlyout(key)))}
          </MuiMenu>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={key}>
        <GroupHeader
          labelKey={group.labelKey}
          shortLabelKey={group.shortLabelKey}
          icon={group.icon}
          isOpen={isGroupOpen}
          isActive={isActive}
          variant={onClose ? 'flyout' : 'sidebar'}
          onToggle={() => toggleGroup(key, isTopLevel ? null : groupKey(path))}
        />
        <Collapse in={isGroupOpen} timeout='auto' unmountOnExit>
          <List
            component='div'
            disablePadding
            dense
            className={isTopLevel ? classes.subList : classes.subSubList}
          >
            {group.children.map((child) => renderChild(child, childPath, onClose))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const renderEntry = (entry: TopLevelEntry, index: number) => {
    if (entry.type === 'divider')
      return <Divider className={classes.divider} key={`divider-${index}`} variant='middle' />;

    if (entry.type === 'group') return renderGroup(entry, []);

    // top-level route
    const badge = getBadge(entry);
    const disabled = badge === 'notAvailable';
    return (
      <MenuItem
        key={entry.path}
        path={entry.path}
        label={entry.label}
        icon={entry.icon}
        disabled={disabled}
        onClick={entry.collapsesAll ? collapseAll : undefined}
        shortLabelKey={entry.shortLabelKey}
        sidebarOpen={open}
        badge={badge}
        exact={entry.exact}
      />
    );
  };

  return (
    <div className={classes.menuWrapper}>
      <List style={{ width: '100%', maxWidth: 360 }} dense className={classes.container}>
        {menuStructure.map(renderEntry)}
      </List>
      <MenuFooter open={open} />
    </div>
  );
};

export default Menu;
